import { useCallback, useEffect, useRef, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getUuid } from '@kc-console/core';

import { announcementQueries, applyAnnouncementHeartBatch } from '../api/announcement';
import useNotificationApiInstance from './useNotificationApiInstance';

import type { AxiosError } from 'axios';
import type { AnnouncementHeartBatchResult, AnnouncementHeartCount } from '../api/announcement';

/** 마지막 클릭 후 이 시간(ms) 동안 추가 클릭이 없으면 모아둔 하트를 전송합니다. */
const FLUSH_DEBOUNCE_MS = 700;

interface UseApplyAnnouncementHeartBatchOptions {
  /**
   * 전송에 실패했을 때 호출됩니다.
   * @description
   * - 실패한 분량(delta)을 넘겨주므로 화면의 낙관적 카운트를 그만큼 되돌릴 수 있습니다.
   */
  onError?: (error: AxiosError, delta: number) => void;
}

/** 한 번의 배치 요청으로 보내는 값 */
interface ApplyAnnouncementHeartBatchVariables {
  /** 재시도 시 중복 반영을 막는 멱등키 (UUID v4) */
  batchId: string;
  /** 모아둔 하트 수 */
  delta: number;
}

interface ApplyAnnouncementHeartBatch {
  /** 하트를 1개 모읍니다. 마지막 호출 후 일정 시간 쉬면 모아둔 수가 한 번에 전송됩니다. */
  addHeart: () => void;
  /** 모아둔 분량을 즉시 전송합니다. */
  flush: () => void;
  /** 아직 서버로 보내지 않고 모아둔 하트 수 */
  pendingCount: number;
}

/**
 * 연타로 누른 하트를 모아 한 번의 요청으로 반영합니다.
 * @description
 * - 마지막 클릭 후 700ms 쉬면 그때까지 모아둔 수를 한 번에 보냅니다. 연타해도 요청이 폭주하지 않습니다.
 * - batchId(UUID v4)로 멱등성이 보장되므로 재시도해도 중복 반영되지 않습니다.
 * - 성공하면 해당 새소식의 하트 수 캐시를 서버 값과 기존 값 중 큰 쪽으로 맞춥니다.
 *   반영이 늦어 서버 값이 뒤늦게 도착해도 화면의 숫자가 뒤로 줄지 않습니다.
 * - 언마운트(새소식 전환·모달 닫기) 시 남은 분량을 즉시 전송해 유실을 막습니다.
 */
const useApplyAnnouncementHeartBatch = (
  announcementId: string,
  options?: UseApplyAnnouncementHeartBatchOptions,
): ApplyAnnouncementHeartBatch => {
  const queryClient = useQueryClient();
  const { instance } = useNotificationApiInstance();
  const [pendingCount, setPendingCount] = useState(0);

  const { onError } = options ?? {};

  // 제네릭을 명시하지 않으면 에러 타입이 기본값 Error 로 잡혀 onError 로 넘길 수 없습니다.
  const { mutate } = useMutation<
    AnnouncementHeartBatchResult,
    AxiosError,
    ApplyAnnouncementHeartBatchVariables
  >({
    mutationFn: ({ batchId, delta }) =>
      applyAnnouncementHeartBatch(instance, announcementId, { batchId, delta }),
    onSuccess: data => {
      const queryKey = [...announcementQueries.hearts(), announcementId] as const;

      queryClient.setQueryData<AnnouncementHeartCount>(queryKey, previous => ({
        heartCount: Math.max(previous?.heartCount ?? 0, data.heartCount),
      }));
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error, variables) => {
      onError?.(error, variables.delta);
    },
  });

  const pendingRef = useRef(0);
  const flushRef = useRef<(() => void) | undefined>(undefined);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const flush = useCallback(() => {
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = undefined;

    const delta = pendingRef.current;
    if (delta <= 0) return;

    pendingRef.current = 0;
    setPendingCount(0);
    mutate({ batchId: getUuid(), delta });
  }, [mutate]);

  // flush 는 매 렌더 새로 만들어지므로, 언마운트 정리에서는 ref 로 최신 것을 집어 호출합니다.
  useEffect(() => {
    flushRef.current = flush;
  });

  useEffect(() => {
    return () => {
      flushRef.current?.();
    };
  }, []);

  const addHeart = useCallback(() => {
    pendingRef.current += 1;
    setPendingCount(pendingRef.current);

    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => flushRef.current?.(), FLUSH_DEBOUNCE_MS);
  }, []);

  return { addHeart, flush, pendingCount };
};

export default useApplyAnnouncementHeartBatch;
